<template>
  <div class="space-y-4">
    <!-- Main Search Bar -->
    <div class="flex items-center space-x-3">
      <div class="relative flex-1 max-w-lg">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <TextInput
          :validate-on-input="true"
          label=""
          :classes="'w-full'"
          input-classes="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
          name="search"
          type="text"
          :placeholder="$t('Search emails by subject, sender, or content...')"
          @update:modelValue="
            ($event) => {
              search_text = $event
            }
          " />
      </div>

      <!-- Filter Toggle Button -->
      <button
        :class="[
          email_filter
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
        ]"
        class="inline-flex items-center px-4 py-2.5 border rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        @click="email_filter = !email_filter">
        <Funnel class="h-4 w-4 mr-2" />
        {{ email_filter ? $t("Hide Filters") : $t("Show Filters") }}
      </button>

      <!-- Clear All Filters Button -->
      <button
        v-if="filter_changed"
        class="inline-flex items-center px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
        @click="reset">
        <X class="h-4 w-4 mr-1" />
        {{ $t("Clear All") }}
      </button>
    </div>

    <!-- Advanced Filters Panel -->
    <FadeTransition>
      <div v-if="email_filter" class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Contact Filter -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ activeTab == "sent" ? $t("Sent To") : $t("From") }}
            </label>
            <ContactsSelect v-model:selected-contacts="selected_contacts" :label="''" class="w-full" :label-style="0" />
          </div>

          <!-- Date Range Filter -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ $t("Date Range") }}
            </label>
            <DatePicker
              v-model="date_range"
              :is-dark="$store.state.ui.darkMode"
              :input-debounce="500"
              mode="range"
              class="w-full"
              :attributes="[
                {
                  key: 'today',
                  dot: true,
                  dates: new Date()
                }
              ]"
              :popover="{ visibility: 'click' }"
              is-range
              range>
              <template #default="{ inputValue, inputEvents }">
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>

                  <button
                    v-if="date_range && date_range.start"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center"
                    type="button"
                    @click="date_range = null">
                    <X class="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                  </button>

                  <input
                    :value="date_range && date_range.start ? `${inputValue.start} - ${inputValue.end}` : ''"
                    :placeholder="$t('Select date range')"
                    class="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                    v-on="inputEvents.start || inputEvents.end"
                    @input="
                      $utils.debounce(() => {
                        date_range = $event.target.value
                      }, 800)
                    ">
                </div>
              </template>
            </DatePicker>
          </div>
        </div>

        <!-- Filter Summary -->
        <div v-if="filter_changed" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm text-gray-600 dark:text-gray-400">{{ $t("Active filters:") }}</span>

            <span
              v-if="search_text"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
              {{ $t("Text") }}: "{{ search_text }}"
              <button class="ml-1 hover:text-blue-600" @click="search_text = ''">
                <X class="h-3 w-3" />
              </button>
            </span>

            <span
              v-if="selected_contacts.length"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
              {{ $t("Contacts") }}: {{ selected_contacts.length }}
              <button class="ml-1 hover:text-green-600" @click="selected_contacts = []">
                <X class="h-3 w-3" />
              </button>
            </span>

            <span
              v-if="date_range"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
              {{ $t("Date Range") }}
              <button class="ml-1 hover:text-purple-600" @click="date_range = null">
                <X class="h-3 w-3" />
              </button>
            </span>
          </div>
        </div>
      </div>
    </FadeTransition>
  </div>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"
import { Funnel, X } from "lucide-vue-next"
// import FiltersIcon from "@/components/shared/icons/FiltersIcon.vue"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import FadeTransition from "@/components/shared/transitions/FadeTransition.vue"

import { DatePicker } from "v-calendar"

export default {
  components: {
    TextInput,
    Funnel,
    //  FiltersIcon,
    ContactsSelect,
    DatePicker,
    X,
    FadeTransition
  },
  emits: ["fetchEmails"],

  data() {
    return {
      email_filter: false,
      search_text: "",
      filter_changed: false,
      selected_contacts: [],
      date_range: null,
      dont_trigger: false
    }
  },

  computed: {
    activeTab() {
      if (this.$route.query.tab) {
        return this.$route.query.tab
      } else {
        return "inbox"
      }
    }
  },
  watch: {
    search_text() {
      if (!this.dont_trigger) {
        this.setFilterChanged()
      }
      this.filterEmails()
    },
    selected_contacts: {
      deep: true,
      handler() {
        if (!this.dont_trigger) {
          this.setFilterChanged()
        }
        this.filterEmails()
      }
    },
    date_range() {
      if (!this.dont_trigger) {
        this.setFilterChanged()
      }
      this.filterEmails()
    },
    email_filter() {
      if (!this.email_filter) {
        this.reset()
      }
    }
  },

  created() {
    this.filterEmails()
  },
  unmounted() {
    this.$emitter.off("scrollEndEmails")
  },

  methods: {
    reset() {
      this.dont_trigger = true
      this.email_filter = false
      this.search_text = ""
      this.selected_contacts = []
      this.date_range = null
      setTimeout(() => {
        this.dont_trigger = false
      }, 500)

      this.filterEmails()
    },
    setFilterChanged() {
      if (this.search_text || this.selected_contacts.length || this.date_range) {
        this.filter_changed = true
      } else {
        this.filter_changed = false
      }
    },
    filterEmails() {
      this.dont_trigger = true
      let params = {
        page_size: 15,
        expand: "record,contacts",
        ...{ type: "email" },
        ...{ list_name: this.activeTab ? this.activeTab : "inbox" },
        ...(!!this.search_text && { name: this.search_text }),
        ...(!!this.selected_contacts && { contacts: this.selected_contacts.map((c) => c.id).join(",") })
      }
      if (this.date_range) {
        params["interaction_date_start"] = this.$moment(this.date_range.start).startOf("day").toISOString()
        params["interaction_date_end"] = this.$moment(this.date_range.end).endOf("day").toISOString()
      }
      params["prevent_loading"] = true
      this.$emit("fetchEmails", params)
    }
  }
}
</script>
